import { HStack, Text } from "@chakra-ui/layout";
import {
  Avatar,
  type BodyColumnDef,
  BrandLogo,
  DataTable,
  Link,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  type ClientsProjectListTableDataQuery,
  UserScopeEnum,
} from "@src/__generated__/graphql";
import { ProjectOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import Column from "@src/components/modules/projects/list/Table/column";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React from "react";

type TProject = NonNullable<
  ClientsProjectListTableDataQuery["projects"]
>["data"][0];

const getColumns = (): BodyColumnDef<TProject, ProjectOrderByColumnEnum>[] => [
  {
    id: "brand",
    meta: { label: t`Brand` },
    header: () => t`Brand`,
    cell: ({ row }) => (
      <BrandLogo src={row.original.brand.image?.urls.thumbnail} />
    ),
  },
  {
    id: "title",
    meta: { label: t`Project` },
    header: () => t`Project`,
    orderBy: ProjectOrderByColumnEnum.ProjectCode,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <HStack>
          <Link href={{ pathname: "/projects/detail", query: { id: r.id } }}>
            <Text className="truncate" fontSize="md">
              {r.title}
            </Text>
            <Text fontSize="xs">{r.code}</Text>
          </Link>
        </HStack>
      );
    },
  },
  {
    id: "projectManager",
    meta: { label: t`PM` },
    header: () => t`PM`,
    orderBy: ProjectOrderByColumnEnum.ProjectManager,
    cell: ({ row }) => {
      const { projectManager } = row.original;

      return (
        <HStack spacing="2">
          <Avatar
            name={projectManager?.full_name}
            src={projectManager?.image?.urls.thumbnail}
            colorScheme={projectManager?.profile?.hex_color}
          />
          <Text>{projectManager?.full_name}</Text>
        </HStack>
      );
    },
  },
  {
    id: "clients",
    meta: { label: t`Clients` },
    header: () => t`Clients`,
    cell: ({ row }) =>
      row.original.users
        .filter((u) => u.scope?.value === UserScopeEnum.Client)
        .map((u) => u.full_name)
        .join(", "),
  },
  {
    id: "invoicedPercentage",
    meta: { align: "center", label: t`Invoiced` },
    header: () => t`Invoiced`,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <Column.Invoiced
          searchParam={r.code}
          percent={+(r.stats?.invoiced_percent ?? 0)}
        />
      );
    },
  },
];

export const ProjectTableForClients = observer(
  function ProjectTableForClients() {
    const { projectListingForClientsStore: store } = useStore();

    return (
      <React.Fragment>
        <DataTable.Body
          tableKey="projects"
          data={store.projects}
          columns={getColumns()}
          orderedBy={store.orderBy.clauses[0]}
          onOrderByChange={(clause) => {
            if (!clause.order) {
              store.orderBy.setClauses([]);
              return;
            }

            store.orderBy.setClauses([
              { column: clause.column, order: clause.order },
            ]);
          }}
        />
        <Pagination store={store.pagination} />
      </React.Fragment>
    );
  },
);
